import type { PropsWithChildren } from "react";
import React from "react";

import {
  useSearchFormState,
  useSearchFormLabels,
  useIsCompactExpanded
} from "@app/components/search-form/search-form-hooks";
import { SearchFormCompact } from "@app/components/search-form-hydrated/search-form-compact";
import { SearchFormHydrated } from "@app/components/search-form-hydrated/search-form-hydrated";
import { ReactQueryProvider } from "@app/context/react-query-provider";

export const SEARCH_FORM_VARIANTS = [
  "landing-page",
  "results",
  "results-menu"
] as const;

export const SEARCH_FORM_STYLE_VARIANTS = [
  "default",
  "collapse",
  "full_width",
  "form_root",
  "form_root_date_tabs"
] as const;

export type SearchFormVariant = (typeof SEARCH_FORM_VARIANTS)[number];
export type SearchFormStyleVariant =
  (typeof SEARCH_FORM_STYLE_VARIANTS)[number];

export interface Props {
  variant?: SearchFormVariant;
  style_variant?: SearchFormStyleVariant;
  one_way_only?: boolean;
  with_affiliate_checkbox?: boolean;
  className?: string;
  profile_ids?: number[];
}

const ServerWrapper: React.FC<PropsWithChildren<{ className?: string }>> = ({
  children,
  className
}) => {
  const is_server = typeof window === "undefined";

  if (!is_server) {
    return children;
  }

  return (
    <div
      // This wrapper div is used for React root hydration
      id="header-search"
      className={className}
    >
      <ReactQueryProvider>{children}</ReactQueryProvider>
    </div>
  );
};

export const SearchForm: React.FC<Props> = ({
  with_affiliate_checkbox = false,
  variant,
  style_variant,
  one_way_only,
  className,
  profile_ids,
  ...props
}) => {
  const formState = useSearchFormState({
    variant,
    style_variant,
    one_way_only,
    profile_ids
  });
  const search_label = useSearchFormLabels(formState.variant);
  const is_compact_expanded = useIsCompactExpanded();

  // For client-side rendering, check if compact form should be expanded
  const should_render_compact = formState.is_compact && !is_compact_expanded;

  return (
    <ServerWrapper className={className}>
      {should_render_compact ? (
        <SearchFormCompact
          data-variant={formState.variant}
          data-style-variant={formState.style_variant}
          data-profile-ids={JSON.stringify(formState.profile_ids)}
          data-one-way-only={Boolean(formState.one_way_only)}
          with_affiliate_checkbox={with_affiliate_checkbox}
          style_variant={formState.style_variant}
          data-is-compact={formState.is_compact}
          {...props}
        />
      ) : (
        <SearchFormHydrated
          data-variant={formState.variant}
          data-style-variant={formState.style_variant}
          data-profile-ids={JSON.stringify(formState.profile_ids)}
          data-one-way-only={Boolean(formState.one_way_only)}
          with_affiliate_checkbox={with_affiliate_checkbox}
          search_label={search_label}
          compact_configuration={formState.compact_configuration}
          style_variant={formState.style_variant}
          one_way_only={formState.one_way_only}
          profile_ids={formState.profile_ids}
          {...props}
        />
      )}
    </ServerWrapper>
  );
};
